import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Loader2, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { API } from "@/lib/api";
import { OrnamentalDivider } from "@/components/OrnamentalDivider";

export default function CorporateBookingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [cb, setCb] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!sessionId) { setLoading(false); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/corporate/bookings/by-session/${sessionId}`);
        setCb(data);
        if (data.payment_status === "paid" || tries > 8) { setLoading(false); return; }
      } catch (err) {
        if (err?.response?.status === 404) { setLoading(false); return; }
      }
      tries += 1;
      if (tries > 8) { setLoading(false); return; }
      setTimeout(poll, 1500);
    };
    poll();
  }, [sessionId]);

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="corp-processing">
        <Loader2 className="mx-auto animate-spin text-[#6E2638]" size={40} />
        <div className="serif text-3xl text-[#6E2638] mt-6">Confirming your corporate booking…</div>
        <p className="text-[#1A1817]/70 mt-2 text-sm">Stripe is finalising the payment.</p>
      </div>
    );
  }

  if (!cb) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <div className="serif text-3xl text-[#6E2638]">Booking not found.</div>
        <Link to="/corporate" className="btn-primary mt-6 inline-flex">Back to corporate walks</Link>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="corp-success">
      <CheckCircle2 size={56} className="mx-auto text-[#1E4637]" />
      <div className="serif text-4xl md:text-5xl text-[#6E2638] mt-4">Your team walk is booked.</div>
      <OrnamentalDivider label="Payment confirmed" />
      <p className="text-[#1A1817]/80">
        Thanks {cb.contact_name} — we've emailed a confirmation to <b>{cb.contact_email}</b>.
        Martin will personally call you within 24 hours to lock in the date and discuss any team-specific touches.
      </p>
      <div className="card-warm rounded-2xl p-6 mt-8 text-left">
        <div className="text-xs uppercase tracking-[0.24em] text-[#1E4637]">Program</div>
        <div className="serif text-2xl text-[#6E2638] mt-1">{cb.package_title}</div>
        <div className="text-sm mt-2 text-[#1A1817]/80">Company: <b>{cb.company_name}</b></div>
        {cb.tier_label && <div className="text-sm mt-1 text-[#1A1817]/80">Group tier: <b>{cb.tier_label}</b></div>}
        {cb.preferred_date && <div className="text-sm mt-1 text-[#1A1817]/80">Preferred date: <b>{cb.preferred_date}</b></div>}
        {cb.attendee_count && <div className="text-sm mt-1 text-[#1A1817]/80">Attendees: <b>{cb.attendee_count}</b></div>}
        <div className="text-sm mt-1 text-[#1A1817]/80">Paid: <b>AUD ${cb.total_price_aud?.toFixed(2)}</b></div>
      </div>
      <div className="mt-8 flex gap-4 justify-center">
        <Link to="/" className="btn-outline-gold">Back to home</Link>
        <Link to="/corporate" className="btn-primary">Book another walk</Link>
      </div>
    </div>
  );
}
